import { Link } from "@tanstack/react-router";
import { CalendarCheck01 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { useBulkRemoveFromCollaborations } from "@/api/collaborations";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMerchant } from "@/providers/MerchantProvider";
import { error, success, warning } from "@/providers/ToastProvider";
import { getCreatorName } from "@/utils/creator-name";
import { formatCreatorId } from "@/utils/formatters";
import { pluralize } from "@/utils/text";

type TargetCollaboration = {
  collaborationId: string;
  name: string;
  status: string | null;
  endTime?: string | null;
  type?: string | null;
  kind?: "target" | "open" | "invitation" | null;
  membershipStatus?: string | null;
};

type Creator = {
  creatorId: string;
  creatorName?: string;
  nickname?: string | null;
  thumbnail?: string | null;
  targetCollaborations?: TargetCollaboration[];
};

interface RemoveCreatorFromCollabModalProps {
  creator: Creator;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const isCollaborationExpired = (collab: TargetCollaboration): boolean => {
  if (collab.endTime) {
    return new Date(collab.endTime) < new Date();
  }
  return false;
};

const CollaborationLink = ({ collab, merchantId }: { collab: TargetCollaboration; merchantId: string }) => {
  const collabType = collab.type?.includes("TARGET") ? "target" : "open";

  return (
    <Link
      to={`/merchant/$merchantId/collaborations/${collabType}/$collaborationId`}
      params={{ merchantId, collaborationId: collab.collaborationId }}
      className="text-sm text-brand-primary underline underline-offset-2 hover:text-brand-secondary"
      target="_blank"
    >
      {collab.name}
    </Link>
  );
};

const ExpiredCollaborationNotice = ({
  expiredCollaborations,
  merchantId,
}: {
  expiredCollaborations: TargetCollaboration[];
  merchantId: string;
}) => (
  <div className="flex flex-col items-center gap-3 bg-warning-50 rounded-lg px-6 py-8 text-center">
    <CalendarCheck01 className="w-8 h-8 text-tertiary" />
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium text-primary">
        The collaboration{expiredCollaborations.length > 1 ? "s" : ""} this video belongs to{" "}
        {expiredCollaborations.length > 1 ? "have" : "has"} expired
      </p>
      <p className="text-sm text-tertiary">
        {expiredCollaborations.map((collab, index) => (
          <span key={collab.collaborationId}>
            {index > 0 && ", "}
            {collab.type ? (
              <CollaborationLink collab={collab} merchantId={merchantId} />
            ) : (
              <span className="font-medium">{collab.name}</span>
            )}
          </span>
        ))}{" "}
        {expiredCollaborations.length > 1 ? "are" : "is"} no longer active
      </p>
    </div>
  </div>
);

export const RemoveCreatorFromCollabModal = ({
  creator,
  open,
  onOpenChange,
  onSuccess,
}: RemoveCreatorFromCollabModalProps) => {
  const { nickname, handle } = getCreatorName(creator.nickname, creator.creatorName);
  const displayName = nickname ?? handle ?? formatCreatorId(creator.creatorId);
  const merchantId = useMerchant();
  const [selectedCollabIds, setSelectedCollabIds] = useState<string[]>([]);
  const { mutateAsync: removeFromCollaborations, isPending } = useBulkRemoveFromCollaborations();

  const allCollaborations = creator.targetCollaborations || [];

  const { activeCollaborations, expiredCollaborations } = useMemo(() => {
    const active: TargetCollaboration[] = [];
    const expired: TargetCollaboration[] = [];
    for (const collab of allCollaborations) {
      if (isCollaborationExpired(collab)) {
        expired.push(collab);
      } else {
        active.push(collab);
      }
    }
    active.sort((a, b) => a.name.localeCompare(b.name));
    return { activeCollaborations: active, expiredCollaborations: expired };
  }, [allCollaborations]);

  const hasActiveCollaborations = activeCollaborations.length > 0;
  const allExpired = allCollaborations.length > 0 && activeCollaborations.length === 0;

  useEffect(() => {
    if (open) {
      setSelectedCollabIds([]);
    }
  }, [open]);

  const handleToggleCollab = (collabId: string) => {
    setSelectedCollabIds((prev) =>
      prev.includes(collabId) ? prev.filter((id) => id !== collabId) : [...prev, collabId],
    );
  };

  const handleToggleAll = () => {
    if (selectedCollabIds.length === activeCollaborations.length) {
      setSelectedCollabIds([]);
    } else {
      setSelectedCollabIds(activeCollaborations.map((c) => c.collaborationId));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedCollabIds.length === 0) {
      error("Please select at least one collaboration to remove from");
      return;
    }

    try {
      const collaborationInfo = {
        openCollaborations: [],
        targetCollaborations: selectedCollabIds
          .map((collabId) => {
            const collab = activeCollaborations.find((c) => c.collaborationId === collabId);
            if (!collab) return null;
            return {
              collaborationId: collabId,
              name: collab.name,
              productIds: [],
              creatorIds: [creator.creatorId],
              creatorCount: 1,
              endTime: collab.endTime ?? null,
            };
          })
          .filter((c): c is NonNullable<typeof c> => c !== null),
        lastSyncedAt: null,
      };

      const result = await removeFromCollaborations({
        openCollaborationIds: [],
        targetCollaborationIds: selectedCollabIds,
        collaborationInfo,
      });

      const collabCount = selectedCollabIds.length;
      const collabsText = pluralize(collabCount, "collaboration", "collaborations");
      const removedCount = result.summary.totalRemoved;
      const failedCount = result.summary.totalFailed;

      if (failedCount > 0 && removedCount === 0) {
        warning("Removal failed", {
          description: `Could not remove ${displayName} from ${collabsText}. At least one creator must remain in each collaboration.`,
        });
      } else if (failedCount > 0) {
        warning("Partially completed", {
          description: `Removed from ${removedCount} of ${collabCount} ${collabsText}. Some collaborations require at least one creator.`,
        });
      } else {
        success("Success", {
          description: `Removed ${displayName} from ${collabsText}`,
        });
        onSuccess?.();
        onOpenChange(false);
        setSelectedCollabIds([]);
      }
    } catch (err) {
      error(err instanceof Error ? err.message : "Failed to remove creator from collaborations");
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedCollabIds([]);
    }
    onOpenChange(newOpen);
  };

  const allSelected = selectedCollabIds.length === activeCollaborations.length && activeCollaborations.length > 0;
  const someSelected = selectedCollabIds.length > 0 && !allSelected;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[40rem]">
        <form onSubmit={handleSubmit} className="space-y-6">
          <DialogHeader>
            <DialogTitle>Remove {displayName} from collaboration</DialogTitle>
            <DialogDescription>
              Select the collaborations you want to remove this creator from. Note that at least one creator must remain
              in each collaboration.
            </DialogDescription>
          </DialogHeader>

          {allExpired ? (
            <ExpiredCollaborationNotice expiredCollaborations={expiredCollaborations} merchantId={merchantId} />
          ) : (
            <>
              <div className="flex flex-col max-h-[20rem] overflow-auto border border-border-secondary rounded-lg">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary bg-secondary sticky top-0">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      isSelected={allSelected}
                      isIndeterminate={someSelected}
                      isDisabled={!hasActiveCollaborations || isPending}
                      aria-label="Select all collaborations"
                      onChange={handleToggleAll}
                    />
                    <span className="text-sm text-tertiary">Collaboration name</span>
                  </div>
                  <span className="text-sm text-tertiary">Status</span>
                </div>

                {!hasActiveCollaborations ? (
                  <div className="text-center text-tertiary py-8">This creator is not in any collaborations.</div>
                ) : (
                  activeCollaborations.map((collab) => {
                    const isSelected = selectedCollabIds.includes(collab.collaborationId);
                    return (
                      <button
                        key={collab.collaborationId}
                        type="button"
                        className="flex items-center justify-between px-4 py-4 border-b border-border-secondary last:border-b-0 cursor-pointer hover:bg-secondary/50 w-full text-left"
                        onClick={() => handleToggleCollab(collab.collaborationId)}
                        disabled={isPending}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox isSelected={isSelected} isDisabled={isPending} />
                          <span className="text-sm text-primary">{collab.name}</span>
                        </div>
                        <span className="text-sm text-primary capitalize">
                          {collab.status === "NORMAL" ? "Active" : (collab.status ?? "").toLowerCase()}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>

              {expiredCollaborations.length > 0 && (
                <ExpiredCollaborationNotice expiredCollaborations={expiredCollaborations} merchantId={merchantId} />
              )}

              {selectedCollabIds.length > 0 && (
                <div className="flex items-center gap-3 bg-warning-50 rounded-lg px-4 py-3">
                  {creator.thumbnail ? (
                    <img src={creator.thumbnail} alt={displayName} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary">
                        {displayName?.charAt(0).toUpperCase() || ""}
                      </span>
                    </div>
                  )}
                  <span className="text-sm text-secondary">
                    <span className="font-semibold">{displayName}</span> will be removed from the above collaborations
                  </span>
                </div>
              )}
            </>
          )}

          <DialogFooter>
            <Button color="secondary" size="lg" onClick={() => handleOpenChange(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button
              color="primary-destructive"
              size="lg"
              type="submit"
              isDisabled={allExpired || !hasActiveCollaborations || isPending}
              isLoading={isPending}
            >
              Remove from {pluralize(selectedCollabIds.length || 0, "collaboration", "collaborations")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
